import { useKlassCategoryItemsListQuery } from 'features/catalog/queries/useKlassCategoryItemsListQuery'
import { IKlassCategory } from 'features/catalog/types'
import { isEmpty } from 'lodash'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import styles from './CatalogMenu.module.scss'

interface ICatalogMenuItemProps {
  menuType: 'button' | 'imageButton'
  data: IKlassCategory
  curCategory: string
  onCategoryClick: (category: string) => void
}

interface ICatalogMenuProps {
  menuType: 'button' | 'imageButton'
  category: string
  klassCategoryList: IKlassCategory[]
  isScrolling: boolean
}

const CatalogMenu = ({ menuType, category, klassCategoryList, isScrolling }: ICatalogMenuProps) => {
  const router = useRouter()
  const scrollRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const curCategoryIndex = klassCategoryList.findIndex(item => item.category === category)
  const [isAtLastIndex, setIsAtLastIndex] = useState(true)

  const handleScroll = () => {
    if (scrollRef.current) {
      const currentScrollRef = scrollRef.current
      const { scrollWidth, clientWidth, scrollLeft } = currentScrollRef
      setIsAtLastIndex(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth)
    }
  }

  const handleCategoryClick = (category: string) => {
    void router.push({
      pathname: '/catalog',
      query: { ...router.query, category, sort: '-order' },
    })
  }

  const scrollToIndex = (index: number) => {
    const selectedElement = itemRefs.current[index]
    if (selectedElement) {
      selectedElement.parentElement?.scrollTo({
        left: selectedElement.offsetLeft - 16,
        behavior: 'smooth',
      })
    }
  }

  useLayoutEffect(() => {
    if (curCategoryIndex !== -1) {
      const observer = new MutationObserver(() => {
        if (itemRefs.current[curCategoryIndex]) {
          setTimeout(() => scrollToIndex(curCategoryIndex), 100)
          observer.disconnect()
        }
      })

      if (scrollRef.current) {
        observer.observe(scrollRef.current, { childList: true, subtree: true })
      }

      return () => observer.disconnect()
    }
  }, [category, klassCategoryList, curCategoryIndex])

  useEffect(() => {
    if (isScrolling) {
      if (curCategoryIndex !== -1) {
        scrollToIndex(curCategoryIndex)
      }
    }
  }, [isScrolling, curCategoryIndex, klassCategoryList])

  return (
    <>
      {menuType === 'button' ? (
        <>
          <div className="w-full bg-white">
            <div
              ref={scrollRef}
              className={`mx-auto flex space-x-2 overflow-x-auto px-4 py-3 md:justify-center md:space-x-2 md:px-6 lg:max-w-7xl lg:space-x-4 ${styles.scrollHidden}`}>
              {klassCategoryList.map((data, idx) => (
                <div key={`catalog_${idx}_button`} ref={el => (itemRefs.current[idx] = el)}>
                  <CatalogMenuItem
                    menuType="button"
                    curCategory={category}
                    data={data}
                    onCategoryClick={handleCategoryClick}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={`h-6 ${styles.menuShadow}`} />
        </>
      ) : (
        <div className="relative w-full bg-white">
          {isAtLastIndex && <div className={`md:hidden ${styles.menuScrollableShadow}`} />}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`mx-auto flex overflow-x-auto pr-4 md:justify-center md:space-x-1 md:px-4 lg:max-w-7xl lg:space-x-2 ${styles.scrollHidden}`}>
            {klassCategoryList.map((data, idx) => (
              <div key={`catalog_${idx}_image_button`} ref={el => (itemRefs.current[idx] = el)}>
                <CatalogMenuItem
                  menuType="imageButton"
                  curCategory={category}
                  data={data}
                  onCategoryClick={handleCategoryClick}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default CatalogMenu

const CatalogMenuItem = ({
  menuType,
  data,
  curCategory,
  onCategoryClick,
}: ICatalogMenuItemProps) => {
  const { category, name, image } = data
  const { data: klassCategoryItems } = useKlassCategoryItemsListQuery('catalog', category)
  const isCurrent = curCategory === category
  const showMenu = useMemo(() => !isEmpty(klassCategoryItems?.data), [
    curCategory,
    klassCategoryItems,
  ])

  // if (!showMenu) return null

  return (
    <>
      {menuType === 'button' ? (
        <button
          onClick={() => onCategoryClick(category)}
          className={`rounded-full px-3 py-2 ${
            isCurrent ? 'borer border-gray-800 bg-gray-800 text-white' : 'border border-gray-200'
          }`}>
          <span className="whitespace-nowrap break-words break-keep text-sm md:text-base">
            {name}
          </span>
        </button>
      ) : (
        <button
          onClick={() => onCategoryClick(category)}
          className="flex w-[84px] min-w-[84px] flex-col items-center space-y-2 md:w-[96px] md:min-w-[96px]">
          <div
            className={`relative h-12 w-12 overflow-hidden rounded-[18px] lg:h-16 lg:w-16 lg:rounded-3xl ${
              isCurrent ? styles.imageButtonShadow : ''
            }`}>
            <Image
              src={
                image ||
                'https://d35ai18pny966l.cloudfront.net/course/notice_boards/empty_file_img.png'
              }
              alt="catalog_category_img"
              layout="fill"
              className="object-cover"
            />
          </div>
          <div
            className={`whitespace-nowrap break-words break-keep text-sm md:text-base ${
              isCurrent ? 'font-semibold text-yellow-450' : 'text-gray-500'
            }`}>
            {name}
          </div>
        </button>
      )}
    </>
  )
}
